import { Dices, Heart } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { MarkPicker } from "@/book/components/mark-picker";
import { ExerciseChips } from "@/book/components/rows";
import { ExperienceForm } from "@/book/components/experience-form";
import { ExperienceList } from "@/book/components/experience-list";
import { errorMessage } from "@/shared/api/client";
import { useDailyPick, useExercise, useSaveMark } from "@/shared/api/book";
import { track } from "@/shared/lib/track";
import { AudioPlayer } from "@/shared/ui/audio-player";
import { Button } from "@/shared/ui/button";
import { Textarea } from "@/shared/ui/input";
import { Markdown } from "@/shared/ui/markdown";
import { SubHeader } from "@/shared/ui/page-header";
import { PromptBlock } from "@/shared/ui/prompt-block";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";
import { ErrorNote, ListSkeleton } from "@/shared/ui/state";
import { cn } from "@/shared/lib/utils";

/** Gyakorlat-részlet (design-rendszer 7.): chipek → cím → lejátszó → leírás → prompt →
 *  állapot-választó → privát jegyzet → kapcsolódó tapasztalatok.
 *
 *  A fejléc chevronja oda visz vissza, ahonnan az olvasó jött (`SubHeader`); a Gyakorlattár
 *  csak előzmény nélkül a cél. Ha ez a ma kihúzott gyakorlat, a cím fölött „Mai
 *  gyakorlatod" szemcímke áll — bárhonnan érkezett is ide. */
export function ExerciseDetailPage() {
  const { exerciseId } = useParams();
  const id = Number(exerciseId);
  const { data: exercise, isPending, isError } = useExercise(id);
  const save = useSaveMark(id);
  // a menü ugyanezt a lekérdezést használja (`useOpenDaily`), ezért ez nem külön hívás
  const { data: daily } = useDailyPick();
  const isDaily = daily?.exercise?.id === id;

  const [note, setNote] = useState("");
  const [noteDirty, setNoteDirty] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    if (exercise && !noteDirty) setNote(exercise.mark.note ?? "");
  }, [exercise, noteDirty]);

  if (isPending) return <ListSkeleton rows={5} />;
  if (isError || !exercise) return <ErrorNote>Ez a gyakorlat nem érhető el.</ErrorNote>;

  const saveNote = () =>
    save.mutate({ note: note.trim() || null }, { onSuccess: () => setNoteDirty(false) });

  return (
    <div className="flex flex-col gap-5">
      <SubHeader
        to="/app/book/gyakorlatok"
        parent="Gyakorlattár"
        right={
          <button
            type="button"
            aria-label={exercise.mark.is_favorite ? "Kedvenc törlése" : "Kedvencnek jelölöm"}
            aria-pressed={exercise.mark.is_favorite}
            onClick={() => save.mutate({ is_favorite: !exercise.mark.is_favorite })}
            className="-mr-2 flex h-11 w-11 items-center justify-center rounded-md"
          >
            <Heart
              size={24}
              className={cn(exercise.mark.is_favorite ? "text-gold" : "text-muted")}
              fill={exercise.mark.is_favorite ? "currentColor" : "none"}
            />
          </button>
        }
      />

      <div className="flex flex-col gap-3">
        {isDaily && (
          <span className="section-label flex items-center gap-1.5 text-gold-text">
            <Dices size={14} strokeWidth={2.2} aria-hidden />
            Mai gyakorlatod
          </span>
        )}
        <ExerciseChips exercise={exercise} />
        <h1 className="text-[28px] font-extrabold leading-[1.1] tracking-[-0.01em]">
          {exercise.title}
        </h1>
      </div>

      {exercise.audio_url && (
        <AudioPlayer
          src={exercise.audio_url}
          title={exercise.title}
          seconds={exercise.audio_seconds}
          onStart={() => track("book.meditation.play", exercise.id)}
          onComplete={() => track("book.meditation.complete", exercise.id)}
        />
      )}

      {exercise.body && <Markdown>{exercise.body}</Markdown>}

      {exercise.needs_ai && exercise.ai_prompt && <PromptBlock prompt={exercise.ai_prompt} />}

      <section className="flex flex-col gap-2">
        {/* A választó feliratai önmagukért beszélnek; a csoport neve a képernyőolvasónak
            a `MarkPicker` `aria-label`-jében marad meg. */}
        <MarkPicker
          value={exercise.mark.status}
          onChange={(status) => save.mutate({ status })}
          disabled={save.isPending}
        />
      </section>

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <SectionLabel>Privát jegyzet</SectionLabel>
          <span className="text-[11px] text-muted">Csak te látod</span>
        </div>
        <Textarea
          aria-label="Privát jegyzet"
          className="min-h-24"
          value={note}
          placeholder="Mi történt, amikor kipróbáltad?"
          onChange={(e) => {
            setNote(e.target.value);
            setNoteDirty(true);
          }}
        />
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            variant="primary"
            block
            disabled={!noteDirty || save.isPending}
            onClick={saveNote}
          >
            {save.isPending ? "Mentés…" : "Jegyzet mentése"}
          </Button>
          <Button
            variant="outline"
            block
            disabled={note.trim().length < 10}
            onClick={() => setShareOpen(true)}
          >
            Megosztom tapasztalatként
          </Button>
        </div>
        {save.isError && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
      </section>

      <ExperienceList
        exerciseId={exercise.id}
        label="Ehhez a gyakorlathoz tartozó tapasztalatok"
        emptyText="Ehhez a gyakorlathoz még nincs megosztott tapasztalat."
        shareLabel="Megosztom a tapasztalatom"
      />

      <Sheet
        open={shareOpen}
        onOpenChange={setShareOpen}
        title="Megosztom tapasztalatként"
        description="A jegyzeted a szokásos jóváhagyási körrel jelenik meg, ehhez a gyakorlathoz kapcsolva."
      >
        <ExperienceForm
          initialBody={note}
          exerciseId={exercise.id}
          chapterId={exercise.chapters[0]?.id}
          onDone={() => setShareOpen(false)}
        />
      </Sheet>
    </div>
  );
}
